import { FolderOpen } from "lucide-react";
import { useEffect, useState } from "react";
import { DialogTrigger } from "react-aria-components";

import { isUserLoggedIn } from "@design-lab/entities/user";
import { cn } from "@design-lab/shared/lib";
import { Button, Popover } from "@design-lab/shared/ui";
import { Tooltip, TooltipContent, TooltipTrigger } from "@design-lab/shared/ui/tooltip";
import { useSavedProjectsTooltipListener } from "@design-lab/features/save-project/hooks/useSaveProject";
import { useResponsive } from "@design-lab/shared/hooks/useResponsive";
import { SavedProjectsContent } from "./saved-projects";

export function SavedProjectsButton() {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const isLoggedIn = isUserLoggedIn();
  const { showTooltip, hideTooltip } = useSavedProjectsTooltipListener();
  const { isDesktop } = useResponsive();

  useEffect(() => {
    if (isPopoverOpen && showTooltip) {
      hideTooltip();
    }
  }, [isPopoverOpen, showTooltip, hideTooltip]);

  if (!isLoggedIn) {
    return null;
  }

  return (
    <Tooltip open={showTooltip}>
      <TooltipTrigger asChild data-testid="saved-projects-tooltip-trigger">
        <div>
          <DialogTrigger isOpen={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
            <Button
              variant="secondary"
              className={cn(
                "flex flex-col items-center gap-0 border-none bg-transparent p-0 shadow-none lg:h-14 lg:border lg:border-solid lg:border-gray-300 lg:bg-gray-50 lg:p-3 lg:shadow-none",
                isPopoverOpen ? "lg:border-blue-600" : "lg:border-gray-300"
              )}
              data-testid="saved-projects-button"
            >
              <FolderOpen className="size-6" />
              <span className="hidden text-xs lg:inline">Saved Projects</span>
            </Button>
            <Popover
              className="max-h-[80vh] w-[95vw] overflow-hidden p-0 lg:w-auto"
              placement="bottom start"
            >
              <div className="flex max-h-[80vh] flex-col">
                <div className="min-h-0 flex-1 overflow-y-auto">
                  <SavedProjectsContent />
                </div>
              </div>
            </Popover>
          </DialogTrigger>
        </div>
      </TooltipTrigger>
      <TooltipContent
        side="bottom"
        align={isDesktop ? "start" : "center"}
        sideOffset={5}
        onClose={hideTooltip}
        onEscapeKeyDown={hideTooltip}
        className="font-inter text-background bg-blue-600 p-4 text-sm"
        arrowClassName="bg-blue-600 fill-blue-600 size-4 rounded-[5px]"
      >
        <span>
          Your <span className="font-semibold">Saved Projects</span> are available here
        </span>
      </TooltipContent>
    </Tooltip>
  );
}
